/**
 * PrintIssueDialog - Two-phase dialog for reporting print file/design issues
 *
 * Follows RedoReasonDialog pattern:
 * - Phase 1: Reason selection grid with numbered hotkeys (1-6)
 * - Phase 2: Confirmation view with optional notes, Enter hotkey (Escape closes via dialog)
 * - Scanner-proof: onOpenAutoFocus prevented, Back button focused on phase transition
 * - Large touch targets for warehouse gloves
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { AlertTriangle, Loader2 } from 'lucide-react';

export const PRINT_FILE_REASONS = [
  { value: 'wrong_design', label: 'Wrong Design', description: 'Incorrect artwork or design file for this SKU' },
  { value: 'missing_artwork', label: 'Missing Artwork', description: 'No print file found for this product' },
  { value: 'color_mismatch', label: 'Color Mismatch', description: "Print colors don't match the expected design" },
  { value: 'low_resolution', label: 'Low Resolution', description: 'Image quality too low, pixelated or blurry' },
  { value: 'wrong_position', label: 'Wrong Position', description: 'Design is wrong size or positioned incorrectly' },
  { value: 'other', label: 'Other Issue', description: 'Other print file or design data problem' },
] as const;

interface PrintIssueDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  garment: {
    id: string;
    sku: string;
    productName: string;
    size: string | null;
    color: string | null;
  } | null;
  onSubmit: (reason: string, notes?: string) => void;
  isPending?: boolean;
  reasons?: ReadonlyArray<{ value: string; label: string; description: string }>;
  reasonsLoading?: boolean;
}

export function PrintIssueDialog({
  open,
  onOpenChange,
  garment,
  onSubmit,
  isPending = false,
  reasons: reasonsProp,
  reasonsLoading = false,
}: PrintIssueDialogProps) {
  const { t } = useTranslation('production');
  const reasons = reasonsProp ?? PRINT_FILE_REASONS;
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const backButtonRef = useRef<HTMLButtonElement>(null);

  // Focus Back button when transitioning to confirmation phase (scanner-proofing)
  useEffect(() => {
    if (selectedReason && backButtonRef.current) {
      backButtonRef.current.focus();
    }
  }, [selectedReason]);

  // Reset state when dialog closes
  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        setSelectedReason(null);
        setNotes('');
      }
      onOpenChange(isOpen);
    },
    [onOpenChange],
  );

  // Prevent auto-focus on dialog open (scanner-proofing)
  const handleOpenAutoFocus = useCallback((e: Event) => {
    e.preventDefault();
  }, []);

  // Keyboard hotkeys
  // Phase 1: 1-6 selects reason
  // Phase 2: Enter confirms (Escape closes dialog)
  useEffect(() => {
    if (!open || isPending) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.altKey || e.metaKey) return;

      // Don't capture keys when typing in textarea
      const active = document.activeElement;
      if (active?.tagName.toLowerCase() === 'textarea') return;

      if (!selectedReason) {
        // Phase 1: number keys select reason
        const num = parseInt(e.key, 10);
        const reason = num >= 1 && num <= reasons.length ? reasons[num - 1] : undefined;
        if (reason) {
          e.preventDefault();
          e.stopPropagation();
          setSelectedReason(reason.value);
        }
      } else {
        // Phase 2: Enter confirms (Escape closes dialog via Radix default)
        if (e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();
          onSubmit(selectedReason, notes.trim() || undefined);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [open, isPending, selectedReason, notes, onSubmit]);

  if (!garment) return null;

  const selectedReasonData = selectedReason
    ? reasons.find((r) => r.value === selectedReason)
    : null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-lg"
        onOpenAutoFocus={handleOpenAutoFocus}
      >
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center gap-2.5">
            <AlertTriangle className="h-6 w-6 text-destructive" aria-hidden="true" />
            {selectedReason ? t('printer.issue.confirmTitle') : t('printer.issue.title')}
          </DialogTitle>
        </DialogHeader>

        {/* Garment context */}
        <div className="bg-muted/50 rounded-lg p-3">
          <p className="font-medium">{garment.productName}</p>
          <div className="flex gap-2 mt-1">
            <Badge variant="outline" className="text-xs font-mono">
              {garment.sku}
            </Badge>
            {garment.size && (
              <Badge variant="secondary" className="text-xs">
                {garment.size}
              </Badge>
            )}
            {garment.color && (
              <Badge variant="secondary" className="text-xs">
                {garment.color}
              </Badge>
            )}
          </div>
        </div>

        {isPending || reasonsLoading ? (
          <div className="flex flex-col items-center justify-center py-8 min-h-[280px]">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mb-4" />
            <p className="text-muted-foreground">{reasonsLoading ? t('catcher.loadingReasons') : t('reporting')}</p>
          </div>
        ) : selectedReason && selectedReasonData ? (
          /* Phase 2: Confirmation view */
          <div className="flex flex-col gap-4 py-2">
            {/* Selected reason card */}
            <div className="rounded-lg border border-orange-300 bg-orange-50 dark:border-orange-700 dark:bg-orange-950/30 p-4 flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-orange-600 dark:text-orange-400 shrink-0 mt-0.5" />
              <div>
                <p className="text-base font-semibold">{selectedReasonData.label}</p>
                <p className="text-sm text-muted-foreground">
                  {selectedReasonData.description}
                </p>
              </div>
            </div>

            {/* Optional notes */}
            <div className="space-y-2">
              <Label htmlFor="print-issue-notes" className="text-sm font-medium">
                {t('printer.issue.additionalNotes')}
              </Label>
              <Textarea
                id="print-issue-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t('printer.issue.notesPlaceholder')}
                className="h-20 resize-none"
              />
            </div>

            {/* Report Issue button */}
            <Button
              variant="destructive"
              className="w-full h-14 text-lg font-semibold"
              onClick={() => onSubmit(selectedReason, notes.trim() || undefined)}
              disabled={isPending}
            >
              {t('printer.issue.report')}
              <kbd className="ml-2 text-xs font-normal opacity-60 bg-white/20 px-1.5 py-0.5 rounded">Enter</kbd>
            </Button>

            {/* Back button */}
            <Button
              ref={backButtonRef}
              variant="outline"
              className="w-full h-12"
              onClick={() => {
                setSelectedReason(null);
                setNotes('');
              }}
              disabled={isPending}
            >
              {t('common:back')}
            </Button>

            {/* Cancel */}
            <Button
              variant="ghost"
              className="w-full h-12"
              onClick={() => handleOpenChange(false)}
              disabled={isPending}
            >
              {t('common:cancel')}
            </Button>
          </div>
        ) : (
          /* Phase 1: Reason selection grid */
          <div className="flex flex-col">
            <div className="max-h-[60vh] overflow-y-auto py-2">
              <div className="grid grid-cols-2 gap-3">
                {reasons.map((reason, index) => (
                  <Button
                    key={reason.value}
                    variant="outline"
                    className="h-auto min-h-[5rem] flex flex-col items-center justify-center text-center p-3 whitespace-normal hover:border-orange-500 hover:bg-orange-50 dark:hover:bg-orange-950/20 relative"
                    onClick={() => setSelectedReason(reason.value)}
                  >
                    <kbd className="absolute top-2 left-2 text-[10px] font-normal text-muted-foreground border bg-muted px-1.5 py-0.5 rounded min-w-[20px] text-center">
                      {index + 1}
                    </kbd>
                    <span className="font-semibold text-base w-full">
                      {reason.label}
                    </span>
                    <span className="text-xs text-muted-foreground mt-1 leading-tight w-full">
                      {reason.description}
                    </span>
                  </Button>
                ))}
              </div>
            </div>

            <Button
              variant="ghost"
              className="w-full h-12 shrink-0 mt-2"
              onClick={() => handleOpenChange(false)}
            >
              {t('common:cancel')}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
